import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { StageIcon, money } from "@/components/shared";
import { MapPin, Home as HomeIcon, CalendarDays, Check, X, Trophy, Target, BookOpen, Link as LinkIcon } from "lucide-react";
import { ResaleSection } from "@/components/ResaleSection";

const scoreSum = (h, criteria) => criteria.reduce((a, c) => a + (h.scores?.[c] ?? 0), 0);

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border/50 py-1">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right font-medium">{v || "—"}</dd>
    </div>
  );
}

export function PreviewContent({ data }) {
  const { client, roadmap, agent, homes = [], tours = [], resources = {}, guidance = [] } = data;
  const p = client.profile || {};
  const criteria = [...(p.must_haves || []), ...(p.nice_to_haves || [])];
  const pct = Math.round((roadmap.stages.filter((s) => s.status === "complete").length / roadmap.stages.length) * 100);
  const scored = homes.filter((h) => criteria.some((c) => (h.scores?.[c] ?? 0) > 0));
  const topId = scored.length ? scored.reduce((best, h) => (scoreSum(h, criteria) > scoreSum(best, criteria) ? h : best), scored[0]).id : null;

  return (
    <>
      <div>
        <h1 className="font-heading text-3xl font-bold">Welcome, {client.name?.split(" ")[0]} 👋</h1>
        <p className="text-muted-foreground mt-1">Here's where you are, and the homes you're working through.</p>
      </div>

      <Card className="p-6 rounded-2xl" data-testid="preview-roadmap">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-semibold">Your roadmap</h2>
          <span className="text-sm font-semibold text-primary">{pct}% complete</span>
        </div>
        <Progress value={pct} className="h-2 mb-5" />
        <div className="grid sm:grid-cols-2 gap-3">
          {roadmap.stages.map((s) => (
            <div key={s.key} className="flex items-center gap-3 p-2.5 rounded-xl bg-secondary/50">
              <StageIcon status={s.status} className="h-5 w-5" />
              <div>
                <p className="font-medium text-sm">{s.title}</p>
                <p className="text-xs text-muted-foreground capitalize">{s.status.replace("_", " ")}</p>
              </div>
            </div>
          ))}
        </div>
        {agent && <p className="text-xs text-muted-foreground mt-4">Your agent: <span className="font-medium text-foreground">{agent.name}</span></p>}
      </Card>

      <section>
        <h2 className="font-heading text-xl font-semibold mb-3">My buying profile</h2>
        {!p.completed ? (
          <Card className="p-6 rounded-2xl text-sm text-muted-foreground">Profile not filled in yet.</Card>
        ) : (
          <Card className="p-6 rounded-2xl space-y-4" data-testid="preview-profile">
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <Row k="Budget" v={`${money(p.budget_min)} – ${money(p.budget_max)}`} />
              <Row k="Timeline" v={p.timeline} />
              <Row k="Financing" v={p.financing_status} />
              <Row k="Areas" v={(p.areas || []).join(", ")} />
              <Row k="Property type" v={p.property_type} />
              <Row k="Household" v={p.household_size} />
              <Row k="Workplace" v={p.workplace} />
            </dl>
            {(p.must_haves || []).length > 0 && (
              <div><p className="text-xs font-semibold text-muted-foreground mb-2">Must-haves</p>
                <div className="flex flex-wrap gap-2">{p.must_haves.map((m) => <Badge key={m} className="bg-primary/10 text-primary border-0">{m}</Badge>)}</div>
              </div>
            )}
            {(p.nice_to_haves || []).length > 0 && (
              <div><p className="text-xs font-semibold text-muted-foreground mb-2">Nice-to-haves</p>
                <div className="flex flex-wrap gap-2">{p.nice_to_haves.map((m) => <Badge key={m} className="bg-secondary text-foreground border-0">{m}</Badge>)}</div>
              </div>
            )}
            {p.deal_breakers && <div><p className="text-xs font-semibold text-muted-foreground mb-1">Deal-breakers</p><p className="text-sm">{p.deal_breakers}</p></div>}
            {p.comfort_level && <div><p className="text-xs font-semibold text-muted-foreground mb-1">Consultation notes</p><p className="text-sm whitespace-pre-wrap">{p.comfort_level}</p></div>}
          </Card>
        )}
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold mb-3">Tour schedule</h2>
        {tours.length === 0 ? (
          <Card className="p-6 rounded-2xl text-sm text-muted-foreground">No tours scheduled yet.</Card>
        ) : (
          <div className="space-y-4">
            {tours.map((t, i) => (
              <Card key={t.id} className="rounded-2xl overflow-hidden" data-testid={`preview-tour-${i}`}>
                <div className="grid md:grid-cols-[180px_1fr]">
                  <div className="h-36 md:h-full bg-secondary flex items-center justify-center relative">
                    {t.image_url ? <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" /> : <HomeIcon className="h-8 w-8 text-muted-foreground/40" />}
                    {t.tour_date && <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground border-0 gap-1 text-xs"><CalendarDays className="h-3 w-3" /> {t.tour_date}</Badge>}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading text-lg font-semibold">{t.title}</h3>
                        {t.address && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.address}</p>}
                      </div>
                      {t.price != null && <p className="font-semibold text-primary text-lg">{money(t.price)}</p>}
                    </div>

                    {(t.criteria_meets?.length > 0 || t.criteria_not_meets?.length > 0) && (
                      <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {t.criteria_meets?.length > 0 && (
                          <div className="p-3 rounded-xl bg-status-complete/10 border border-status-complete/20">
                            <p className="text-xs font-semibold text-status-complete flex items-center gap-1 mb-1.5"><Check className="h-3.5 w-3.5" /> Meets your criteria</p>
                            <ul className="space-y-0.5">{t.criteria_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                          </div>
                        )}
                        {t.criteria_not_meets?.length > 0 && (
                          <div className="p-3 rounded-xl bg-status-warning/10 border border-status-warning/20">
                            <p className="text-xs font-semibold text-status-warning flex items-center gap-1 mb-1.5"><X className="h-3.5 w-3.5" /> Doesn't meet</p>
                            <ul className="space-y-0.5">{t.criteria_not_meets.map((m) => <li key={m} className="text-xs text-foreground/80">{m}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    )}

                    <ResaleSection resale={t.resale} showEmpty />

                    {t.details && Object.keys(t.details).length > 0 && (
                      <Accordion type="single" collapsible className="mt-3">
                        <AccordionItem value="details" className="border-0">
                          <AccordionTrigger className="text-sm py-2">Home details</AccordionTrigger>
                          <AccordionContent>
                            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                              {Object.entries(t.details).map(([k, v]) => (
                                <div key={k} className="flex justify-between gap-3 border-b border-border/50 py-1">
                                  <dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{String(v)}</dd>
                                </div>
                              ))}
                            </dl>
                            {t.source_note && <p className="text-[11px] text-muted-foreground mt-3 italic">{t.source_note}</p>}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-heading text-xl font-semibold mb-3">Compare board</h2>
        {homes.length === 0 ? (
          <Card className="p-6 rounded-2xl text-sm text-muted-foreground">No homes shortlisted yet.</Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {homes.map((h, i) => (
              <Card key={h.id} className={`p-5 rounded-2xl ${h.id === topId ? "ring-2 ring-primary" : ""}`} data-testid={`preview-home-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{h.title}</p>
                    {h.address && <p className="text-xs text-muted-foreground">{h.address}</p>}
                  </div>
                  {h.id === topId && <Badge className="bg-primary text-primary-foreground border-0 gap-1 shrink-0"><Trophy className="h-3 w-3" /> Front-runner</Badge>}
                </div>
                {h.price != null && <p className="text-primary font-semibold mt-1">{money(h.price)}</p>}
                {criteria.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Target className="h-3 w-3" /> Scores</p>
                    {criteria.slice(0, 8).map((c) => (
                      <div key={c} className="flex justify-between text-xs"><span className="truncate pr-2">{c}</span><span className="font-medium shrink-0">{h.scores?.[c] ?? 0}/5</span></div>
                    ))}
                  </div>
                )}
                {h.research_notes && <div className="mt-3"><p className="text-xs font-semibold text-muted-foreground mb-1">Research notes</p><p className="text-sm whitespace-pre-wrap">{h.research_notes}</p></div>}
                {(h.pros?.length > 0 || h.cons?.length > 0) && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div><p className="font-semibold text-status-complete mb-1">Pros</p>{(h.pros || []).map((x, j) => <p key={j} className="text-muted-foreground">{x}</p>)}</div>
                    <div><p className="font-semibold text-status-warning mb-1">Cons</p>{(h.cons || []).map((x, j) => <p key={j} className="text-muted-foreground">{x}</p>)}</div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      {guidance.length > 0 && (
        <section>
          <h2 className="font-heading text-xl font-semibold mb-3">Notes from your agent</h2>
          <div className="space-y-2">
            {guidance.map((g) => (
              <Card key={g.id} className="p-4 rounded-2xl"><p className="font-medium text-sm">{g.title}</p><p className="text-sm text-muted-foreground">{g.body}</p></Card>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-heading text-xl font-semibold mb-3">Resources</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {[...(resources.custom || []), ...(resources.default || [])].map((r, i) => (
            <Card key={r.id || i} className="p-4 rounded-2xl" data-testid={`preview-resource-${i}`}>
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{r.category}</p>
                </div>
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="ml-auto text-primary"><LinkIcon className="h-4 w-4" /></a>}
              </div>
            </Card>
          ))}
        </div>
      </section>
    </>
  );
}
